import { z } from 'zod';

/**
 * `cost` is a string, not a number, and stays that way end to end.
 *
 * The column is `numeric(12,2)`; Postgres and Drizzle both surface it as a
 * decimal string so cents survive exactly. Parsing it into a JS number here
 * would reintroduce the binary-float rounding the column type exists to avoid,
 * and `Number('')` silently yields `0` — a missing cost would render as a real
 * $0.00 charge (G15). Format for display, don't coerce for storage. Same
 * contract as `ExpenseCatalogSchema`, which is where these values come from.
 *
 * `description` and `cost` are the customer's OWN copies, seeded from the
 * catalog item at create time and overridable afterwards. They are never
 * re-resolved from the catalog on read: repricing a catalog item must not
 * retroactively change what an existing customer is billed.
 */
export const CustomerExpenseSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  customerId: z.string().uuid(),
  expenseCatalogId: z.string().uuid(),
  description: z.string(),
  /**
   * `null` when the line's catalog item is VARIABLE: such an item has no
   * standing price, so there was nothing to copy down. The amount for a given
   * occurrence is recorded elsewhere, and until it is, invoice generation
   * leaves the line off the invoice rather than billing it. Null means "not
   * known yet", never "free" — do not render it as $0.00.
   */
  cost: z.string().nullable(),
  effectiveFrom: z.string(),
  /** `null` = open-ended: still billed, with no scheduled end. */
  effectiveTo: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CustomerExpense = z.infer<typeof CustomerExpenseSchema>;

/**
 * Cost accepted on the wire as a number or a string. The API canonicalizes it
 * to a two-decimal string and REJECTS more than two decimal places rather than
 * rounding — see the backend `costAmount` validator, which this table shares
 * with the catalog.
 */
const cost = z.union([z.number(), z.string()]);

/**
 * Create one line.
 *
 * `description` and `cost` are optional: omit them and the API seeds both from
 * the referenced catalog item. That is what lets the multi-select "add from
 * catalog" flow post one line per selected item with no per-line form — most
 * lines take the catalog defaults unchanged.
 *
 * For a VARIABLE catalog item there is no price to seed, so `cost` must be
 * omitted — sending one is a 400 on `cost`, not an override.
 */
export const CreateCustomerExpenseInput = z
  .object({
    expenseCatalogId: z.string().uuid(),
    description: z.string().min(1).max(500).optional(),
    cost: cost.optional(),
    effectiveFrom: z.string(),
    effectiveTo: z.string().nullable().optional(),
  })
  .strict();
export type CreateCustomerExpenseInput = z.infer<typeof CreateCustomerExpenseInput>;

/**
 * Partial update. An absent key leaves the stored value alone; there is no
 * value meaning "clear the description" — blank is a 400 — so no patch can
 * leave a line without one.
 *
 * Repointing `expenseCatalogId` does NOT re-seed description or cost; send
 * whatever those should become. Re-seeding server-side would quietly discard
 * an override the user set earlier.
 *
 * `cost` is the one exception, because the target item decides whether an
 * amount is permitted at all: repointing onto a VARIABLE item drops it (and
 * sending one is a 400), while repointing a line that has no amount onto a
 * FIXED item requires one in the same request.
 */
export const PatchCustomerExpenseInput = z
  .object({
    expenseCatalogId: z.string().uuid().optional(),
    description: z.string().min(1).max(500).optional(),
    cost: cost.optional(),
    effectiveFrom: z.string().optional(),
    effectiveTo: z.string().nullable().optional(),
  })
  .strict();
export type PatchCustomerExpenseInput = z.infer<typeof PatchCustomerExpenseInput>;

/** Pagination + as-of snapshot for the per-customer list. */
export interface CustomerExpenseListQuery {
  /** Restrict to lines in force on this date. Omit for full history. */
  asOf?: string;
  limit?: number;
  offset?: number;
}
